import React, { useEffect, useCallback, useMemo } from "react";
import { Switch, Table, Button, Tag, Tooltip, Empty } from "antd";
import { DeleteOutlined, DesktopOutlined, MobileOutlined, TabletOutlined, LaptopOutlined, EnvironmentOutlined } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import StyledModal from "../../components/CustomModal";
import { FooterButtons, StyledButton } from "../../components/styles";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectUsers } from "../../redux/features/user/userSlice";
import { getUserSessions, approveSession, disapproveSession, revokeSession } from "../../redux/features/user/userAsyncThunk";
import { AdminSession } from "../../redux/features/user/types";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { permissions } from "../../utils/constants/permissions";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

type SessionsModalProps = {
  isModalOpen: boolean;
  onCancel: () => void;
  adminId: string;
  adminName: string;
};

const SessionsModal: React.FC<SessionsModalProps> = ({ isModalOpen, onCancel, adminId, adminName }) => {
  const dispatch = useAppDispatch();
  const { sessions, sessionsLoading } = useAppSelector(selectUsers);
  const userRole = useAppSelector(selectUserRole);

  const hasApprovePermission = useMemo(
    () => checkHasPermission(userRole, permissions.ApproveDisApproveSessions),
    [userRole]
  );
  const hasRevokePermission = useMemo(
    () => checkHasPermission(userRole, permissions.RevokeUserSession),
    [userRole]
  );

  useEffect(() => {
    if (isModalOpen && adminId) {
      dispatch(getUserSessions({ adminId }));
    }
  }, [isModalOpen, adminId, dispatch]);

  const handleApprovalToggle = useCallback(
    (session: AdminSession, checked: boolean) => {
      if (checked) {
        dispatch(approveSession({ sessionId: session.id }));
      } else {
        dispatch(disapproveSession({ sessionId: session.id }));
      }
    },
    [dispatch]
  );

  const handleRevoke = useCallback(
    (sessionId: string) => {
      dispatch(revokeSession({ sessionId }));
    },
    [dispatch]
  );

  const getDeviceIcon = (deviceType: string | null) => {
    switch (deviceType?.toLowerCase()) {
      case "mobile":
        return <MobileOutlined style={{ fontSize: 18 }} />;
      case "tablet":
        return <TabletOutlined style={{ fontSize: 18 }} />;
      case "desktop":
        return <DesktopOutlined style={{ fontSize: 18 }} />;
      default:
        return <LaptopOutlined style={{ fontSize: 18 }} />;
    }
  };

  const columns: TableColumnsType<AdminSession> = [
    {
      title: "Device",
      dataIndex: "deviceName",
      key: "deviceName",
      render: (text: string, record: AdminSession) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {getDeviceIcon(record.deviceType)}
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {record.browser} / {record.os}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      render: (text: string) => <span style={{ fontFamily: "monospace" }}>{text}</span>,
    },
    {
      title: "Location",
      key: "location",
      render: (_: any, record: AdminSession) => {
        const location = [record.city, record.state, record.country]
          .filter(Boolean)
          .join(", ");
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <EnvironmentOutlined style={{ color: "#888" }} />
            <span>{location || "Unknown"}</span>
          </div>
        );
      },
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      key: "lastActivityAt",
      render: (text: string) => (
        <Tooltip title={text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : "N/A"}>
          <span>{text ? dayjs(text).fromNow() : "Never"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "isApproved",
      key: "isApproved",
      render: (isApproved: boolean) => (
        <Tag color={isApproved ? "green" : "orange"}>
          {isApproved ? "Approved" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Approved",
      dataIndex: "isApproved",
      key: "approveToggle",
      render: (isApproved: boolean, record: AdminSession) => (
        <Switch
          checked={isApproved}
          onChange={(checked) => handleApprovalToggle(record, checked)}
          disabled={!hasApprovePermission || sessionsLoading}
          loading={sessionsLoading}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: AdminSession) => (
        <Tooltip title="Revoke Session">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRevoke(record.id)}
            disabled={!hasRevokePermission || sessionsLoading}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <StyledModal
      draggable={true}
      width={1100}
      minHeight={"300px"}
      maxHeight={"calc(100dvh - 200px)"}
      title={`Sessions for ${adminName}`}
      open={isModalOpen}
      closeIcon={null}
      footer={
        <FooterButtons>
          <StyledButton onClick={onCancel}>Close</StyledButton>
        </FooterButtons>
      }
      centered
      onCancel={onCancel}
    >
      <div style={{ marginTop: 16 }}>
        {sessions.length === 0 && !sessionsLoading ? (
          <Empty description="No active sessions found" />
        ) : (
          <Table
            columns={columns}
            dataSource={sessions}
            rowKey="id"
            loading={sessionsLoading}
            pagination={false}
            size="small"
          />
        )}
      </div>
    </StyledModal>
  );
};

export default SessionsModal;
